/**
 * Utility functions
 */

import { formatDistanceToNowStrict, formatISO9075 } from "date-fns";

/**
 * Sleep for x milliseconds
 */
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Combine class names, filtering out falsy values
 */
export function classNames(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Column widths for inputs etc
 */
export type COL_WIDTHS = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/**
 * Format a date string to a simplified format
 */
export function simplifyDate(date?: string | null) {
  if (typeof date === "string" && date !== "0001-01-01T00:00:00Z") {
    return formatISO9075(new Date(date));
  }
  return "n/a";
}

/**
 * Format a date string as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date?: string) {
  if (typeof date === "string" && date !== "0001-01-01T00:00:00Z") {
    return formatDistanceToNowStrict(new Date(date), { addSuffix: true });
  }
  return "n/a";
}

/**
 * Convert a string to a URL-safe slug
 */
export function slugify(str: string) {
  return str
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[-\s]+/g, "-");
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string) {
  if ("clipboard" in navigator) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}
